import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "api/apiClient.ts";
interface Mosque {
  _id?: string;
  name: string;
  responsibleName: string;
  mobileNumber: string;
  secondNumber?: string;
  address: string;
  city: string;
  district?: string;
  postalCode?: string;
  isActive?: boolean;
  createdAt?: Date;
  updatedAt?: Date;
  location: {
    type: "Point";
    coordinates: [number, number];
  };
}

interface CreateMosquePayload {
  name: string;
  responsibleName: string;
  mobileNumber: string;
  secondNumber?: string;
  address: string;
  city: string;
  district?: string;
  postalCode?: string;
  isActive?: boolean;
  location: {
    type: "Point";
    coordinates: [number, number];
  };
}

interface UpdateMosquePayload extends CreateMosquePayload {
  _id: string;
}
const fetchMosques = async (searchParams?: { search?: string; sortBy?: string }): Promise<Mosque[]> => {
  const allMosques: Mosque[] = [];
  let page = 1;
  let hasMoreData = true;

  // Build query parameters
  const queryParams = new URLSearchParams();
  queryParams.append('limit', '50');
  if (searchParams?.search) {
    queryParams.append('search', searchParams.search);
  }
  if (searchParams?.sortBy) {
    queryParams.append('sort', searchParams.sortBy);
  }

  while (hasMoreData) {
    queryParams.set('page', page.toString());
    const { data } = await apiClient.get(`/mosques?${queryParams.toString()}`);

    if (data.status !== "success" || !Array.isArray(data.data.mosques)) {
      throw new Error("Invalid response format");
    }

    const mosques = data.data.mosques;
    allMosques.push(...mosques);

    // If we get less than 50 mosques, we've reached the last page
    if (mosques.length < 50) {
      hasMoreData = false;
    } else {
      page++;
    }
  }

  return allMosques;
};
export const useMosques = (searchParams?: { search?: string; sortBy?: string }) => {
  return useQuery({
    queryKey: ["mosques", searchParams],
    queryFn: () => fetchMosques(searchParams),
  });
};
export const useAddMosque = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (mosque: CreateMosquePayload) => {
      await apiClient.post("/mosques/create", mosque);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mosques"] });
    },
  });
};

export const useUpdateMosque = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (mosque: UpdateMosquePayload) => {
      const { _id, ...updateData } = mosque;
      await apiClient.put(`/mosques/${_id}`, updateData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mosques"] });
    },
  });
};
export const useDeleteMosque = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.delete(`/mosques/${id}`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mosques"] });
    },
  });
};
export const useDeleteMosques = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      const { data } = await apiClient.delete("/mosques", {
        data: { ids },
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mosques"] });
    },
  });
};
